import { useFormik } from 'formik';
import React, { useState } from 'react';
import { KeywordTableState } from '../../../components/muiLegacy/Tables/KeywordTable/logic/useKeywordTableState';
import ProjectTable from '../../../components/muiLegacy/Tables/ProjectTable/ProjectTable';
import VoxelAlertDialog from '../../../components/voxel/Dialogs/VoxelAlertDialog';
import VoxelDialog from '../../../components/voxel/Dialogs/VoxelDialog';
import VoxelDialogActionCancel from '../../../components/voxel/Dialogs/VoxelDialogActionCancel';
import VoxelDialogActionSubmit from '../../../components/voxel/Dialogs/VoxelDialogActionSubmit';
import VoxelDialogActions from '../../../components/voxel/Dialogs/VoxelDialogActions';
import VoxelDialogContent from '../../../components/voxel/Dialogs/VoxelDialogContent';
import VoxelLink from '../../../components/voxel/Link/VoxelLink';
import VoxelText from '../../../components/voxel/Text/VoxelText';
import { ProjectGq } from '../../../graphql/projects/models/ProjectGq';
import { ProjectTypeGq } from '../../../graphql/projects/models/ProjectTypeGq';
import { projectsApiService } from '../../../graphql/projects/projectsApiService';
import { GetProjectsCommandGq } from '../../../graphql/projects/queries/useGetProjectsGq';
import { CurrentUserGq } from '../../../graphql/users/models/CurrentUserGq';
import { useDebounceValue } from '../../../logic/otherUtilities/useDebounceValue';
import { ProjectTabChoices } from '../../ProjectPage/ProjectPage';

export interface AddToExistingProjectProps {
  open: boolean;
  onClose: () => void;
  teamId?: number;
  projectId: number;
  locationId?: number;
  languageId?: number;
  user: CurrentUserGq;
  keywordTableState: KeywordTableState;
}

interface FormState {
  sourceProjectId: number;
  targetProjectId: number;
}

export const AddToExistingProjectDialog: React.FC<AddToExistingProjectProps> = ({
  open,
  onClose,
  teamId,
  projectId,
  locationId,
  languageId,
  user,
  keywordTableState
}) => {

  // Get projects
  const [getProjectsFilterState, setGetProjectsFilterState] = useState<GetProjectsCommandGq>({
    types: [ProjectTypeGq.NORMAL_SEARCH],
    excludeIds: [],
    searchTerm: '',
    sortDirection: 'asc',
    sortBy: 'createTime',
    amountPerPage: 8,
    showArchived: false,
    locOrLangFilter: true,
    locationId: locationId,
    languageId: languageId,
  });

  const debouncedFilterState = useDebounceValue(getProjectsFilterState, 300);
  const { query, data: projects } = projectsApiService.queries.useGetProjects(debouncedFilterState);

  // Form
  const addToProjectMutation = projectsApiService.mutations.projects.useAddQuickSearchToProject();
  const formikProps = useFormik<FormState>({
    initialValues: {
      sourceProjectId: projectId,
      targetProjectId: null,
    },
    onSubmit: (values, actions) =>
      addToProjectMutation({
        keywordQuery: {
          filtering: keywordTableState.backendFilterState,
          selecting: keywordTableState.backendSelectionState,
        },
        targetProjectId: values.targetProjectId,
      }).then((targetProject) => setTgQSProject(targetProject)),
  });

  const [tgQSProject, setTgQSProject] = useState<ProjectGq>(null);

  const handleClose = () => {
    formikProps.resetForm();
    onClose();
  };
  const handleCloseSuccess = () => {
    setTgQSProject(null);
    handleClose();
  };

  return (
    <>
      <VoxelDialog
        minWClass="min-w-[1200px]"
        open={open}
        onClose={handleClose}
        closeDisabled={formikProps.isSubmitting}
        title="Add to project"
        subtitle="Selected keywords will be added to the target project"
      >
        <VoxelDialogContent className='mt-4 !pb-0'>
          <ProjectTable
            setFilterState={setGetProjectsFilterState}
            filterState={getProjectsFilterState}
            sortBy={projects?.sortBy}
            sortDirection={projects?.sortDirection}
            projectsData={projects}
            highlightedProjectIds={[formikProps.values.targetProjectId]}
            onClick={(project: ProjectGq) =>
              formikProps.setFieldValue('targetProjectId', project.id)
            }
            allowTeamIdFilter={teamId}
            user={user}
          />
        </VoxelDialogContent>
        <VoxelDialogActions>
          <VoxelDialogActionCancel onClick={handleClose} disabled={formikProps.isSubmitting} />
          <VoxelDialogActionSubmit
            title={'Confirm'}
            onClick={formikProps.submitForm}
            disabled={!formikProps.dirty}
          />
        </VoxelDialogActions>
      </VoxelDialog>
      {tgQSProject != null && (
        <VoxelAlertDialog title='Success' severity='success' open={tgQSProject != null} onClose={handleCloseSuccess}>
          <VoxelDialogContent className='mt-4 !pb-12'>
            <VoxelText>The keywords were successfully added to the project <VoxelLink openInNewIcon openInNewTab href={`/${tgQSProject?.id}/${ProjectTabChoices.Manage}`}>
              {tgQSProject?.name}
            </VoxelLink>
            </VoxelText>
          </VoxelDialogContent>
        </VoxelAlertDialog>
      )}
    </>
  );
};
